import React, { useState, useEffect } from 'react';
import { Modal, Form, DatePicker, Checkbox, Button, Row, Col, Select, Divider } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/pt-br';
import locale from 'antd/locale/pt_BR';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Configurar plugins do dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

interface CPPDRelatorioFiltrosModalProps {
 visible: boolean;
 onCancel: () => void;
 onGenerate: (filtros: any) => void;
 clienteId: string;
 clienteNome: string;
 membrosLista: any[];
 filtrosAtuais: any;
 dashboardData?: any;
}

const CPPDRelatorioFiltrosModal: React.FC<CPPDRelatorioFiltrosModalProps> = ({
 visible,
 onCancel,
 onGenerate,
 clienteId,
 clienteNome,
 membrosLista,
 filtrosAtuais,
 dashboardData
}) => {
 const [form] = Form.useForm();
 const [loading, setLoading] = useState(false);

 useEffect(() => {
   if (visible && !loading) {
     // Usar apenas dayjs com UTC para consistência entre ambientes
     const dataInicioDefault = filtrosAtuais?.dataInicio 
       ? dayjs(filtrosAtuais.dataInicio).utc().startOf('day')
       : dayjs().utc().subtract(6, 'months').startOf('day');
     
     const dataFimDefault = filtrosAtuais?.dataFim 
       ? dayjs(filtrosAtuais.dataFim).utc().endOf('day')
       : dayjs().utc().endOf('day');
     
     const initialValues = {
       periodo: [dataInicioDefault, dataFimDefault],
       incluirEstatisticas: filtrosAtuais?.opcoes?.incluirEstatisticas ?? true,
       incluirReunioes: filtrosAtuais?.opcoes?.incluirReunioes ?? true,
       incluirMembros: filtrosAtuais?.opcoes?.incluirMembros ?? true,
       incluirTimeline: filtrosAtuais?.opcoes?.incluirTimeline ?? true,
       incluirConsideracoes: filtrosAtuais?.opcoes?.incluirConsideracoes ?? true,
       membro_id: filtrosAtuais?.membro_id || null,
       incluirPautas: false,
       incluirTarefas: false
     };
     
     // Log para debug de timezone
     console.log('Timezone do browser:', Intl.DateTimeFormat().resolvedOptions().timeZone);
     console.log('Data início default (UTC):', dataInicioDefault.format());
     console.log('Data fim default (UTC):', dataFimDefault.format());
     
     form.resetFields();
     form.setFieldsValue(initialValues);
   }
 }, [visible, loading, filtrosAtuais]);

 useEffect(() => {
   if (!visible) {
     form.resetFields();
     setLoading(false);
   }
 }, [visible, form]);

 const handleSubmit = async () => {
   try {
     setLoading(true);
     
     const values = await form.validateFields();
     
     const filtrosRelatorio: any = {
       cliente_id: clienteId,
       nomeCliente: clienteNome,
       ...filtrosAtuais
     };
     
     if (values.periodo && Array.isArray(values.periodo) && values.periodo.length === 2) {
       // Garantir formato UTC consistente
       const dataInicio = dayjs(values.periodo[0]).utc().startOf('day');
       const dataFim = dayjs(values.periodo[1]).utc().endOf('day');
       
       filtrosRelatorio.dataInicio = dataInicio.format('YYYY-MM-DD');
       filtrosRelatorio.dataFim = dataFim.format('YYYY-MM-DD');
       
       // Log para debug
       console.log('Período original:', values.periodo);
       console.log('Data início normalizada:', filtrosRelatorio.dataInicio);
       console.log('Data fim normalizada:', filtrosRelatorio.dataFim);
     }
     
     if (values.membro_id) {
       filtrosRelatorio.membro_id = values.membro_id;
     }
     
     filtrosRelatorio.opcoes = {
       incluirEstatisticas: values.incluirEstatisticas ?? true,
       incluirReunioes: values.incluirReunioes ?? true,
       incluirMembros: values.incluirMembros ?? true,
       incluirTimeline: values.incluirTimeline ?? true,
       incluirConsideracoes: values.incluirConsideracoes ?? true,
       incluirPautas: false,
       incluirTarefas: false,
       incluirItensPauta: false,
       incluirCPPDsTotal: false
     };
     
     await onGenerate(filtrosRelatorio);
     onCancel();
   } catch (error) {
     console.error('Erro ao gerar relatório:', error);
   } finally {
     setLoading(false);
   }
 };

 const handleCancel = () => {
   form.resetFields();
   setLoading(false);
   onCancel();
 };

 return (
   <Modal
     title="Opções do Relatório CPPD"
     open={visible}
     onCancel={handleCancel}
     footer={[
       <Button key="cancel" onClick={handleCancel} disabled={loading}>
         Cancelar
       </Button>,
       <Button 
         key="submit" 
         type="primary" 
         loading={loading}
         onClick={handleSubmit}
       >
         Gerar Relatório
       </Button>
     ]}
     width={600}
     destroyOnClose={true}
     maskClosable={false}
   >
     <Form
       form={form}
       layout="vertical"
       preserve={false}
     >
       <Form.Item 
         name="periodo" 
         label="Período do relatório" 
         rules={[{ 
           required: true, 
           message: 'Selecione o período',
           validator: (_, value) => {
             if (!value || !Array.isArray(value) || value.length !== 2) {
               return Promise.reject(new Error('Selecione um período válido'));
             }
             
             // Validação adicional para datas válidas
             const [inicio, fim] = value;
             if (!dayjs(inicio).isValid() || !dayjs(fim).isValid()) {
               return Promise.reject(new Error('Datas inválidas selecionadas'));
             }
             
             return Promise.resolve();
           }
         }]}
       >
         <RangePicker 
           style={{ width: '100%' }} 
           format="DD/MM/YYYY"
           allowClear={false}
           placeholder={['Data inicial', 'Data final']}
           inputReadOnly={true}
           showTime={false}
           picker="date"
           locale={locale.DatePicker}
           disabledDate={(current) => {
             // Usar dayjs UTC para comparação consistente
             return current && current.isAfter(dayjs().utc().endOf('day'));
           }}
           onCalendarChange={(dates) => {
             if (dates && dates.length === 2) {
               // Log com dados UTC para debug
               console.log('Período selecionado:', {
                 inicio: dayjs(dates[0]).utc().format('DD/MM/YYYY'),
                 fim: dayjs(dates[1]).utc().format('DD/MM/YYYY'),
                 inicioISO: dayjs(dates[0]).utc().format(),
                 fimISO: dayjs(dates[1]).utc().format()
               });
             }
           }}
           onChange={(dates) => {
             // Log adicional para debug
             if (dates && dates.length === 2) {
               console.log('Datas alteradas:', {
                 inicio: dayjs(dates[0]).utc().format('YYYY-MM-DD'),
                 fim: dayjs(dates[1]).utc().format('YYYY-MM-DD')
               });
             }
           }}
         />
       </Form.Item>
       
       {membrosLista && membrosLista.length > 0 && (
         <Form.Item 
           name="membro_id" 
           label="Filtrar por membro específico (opcional)"
         >
           <Select
             placeholder="Selecione um membro para filtrar o relatório"
             allowClear
             showSearch
             optionFilterProp="children"
             style={{ width: '100%' }}
             filterOption={(input, option) =>
               (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
             }
           >
             {membrosLista.map(membro => (
               <Option key={membro._id || membro.id} value={membro._id || membro.id}>
                 {membro.nome} ({membro.funcao || 'Membro'})
               </Option>
             ))}
           </Select>
         </Form.Item>
       )}
       
       <Divider>Seções a incluir no relatório</Divider>
       
       <Row gutter={[16, 8]}>
         <Col span={12}>
           <Form.Item name="incluirEstatisticas" valuePropName="checked">
             <Checkbox>Estatísticas Gerais</Checkbox>
           </Form.Item>
         </Col>
         <Col span={12}>
           <Form.Item name="incluirReunioes" valuePropName="checked">
             <Checkbox>Análise de Reuniões</Checkbox>
           </Form.Item>
         </Col>
         <Col span={12}>
           <Form.Item name="incluirMembros" valuePropName="checked">
             <Checkbox>Membros</Checkbox>
           </Form.Item>
         </Col>
         <Col span={12}>
           <Form.Item name="incluirTimeline" valuePropName="checked">
             <Checkbox>Linha do Tempo</Checkbox>
           </Form.Item>
         </Col>
         <Col span={24}>
           <Form.Item name="incluirConsideracoes" valuePropName="checked">
             <Checkbox>Considerações Finais</Checkbox>
           </Form.Item>
         </Col>
       </Row>
     </Form>
   </Modal>
 );
};

export default CPPDRelatorioFiltrosModal;